/** Lookup → Site page. */
import { useCallback, useEffect, useState } from "react";
import { tr } from "@/i18n";
import { MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import { lookupSite } from "@/lib/api";
import { AccessNotesPanel } from "@/components/tech/AccessNotes";
import { Section, Empty, WORow, InspectionRow, ContactRow, VisitRow } from "@/components/tech/lookup/LookupParts";

export default function LookupSite({ siteId, openClient, openWO, openInspection, openMaps }) {
  const [data, setData] = useState(null);
  const [allVisits, setAllVisits] = useState(false);
  const load = useCallback(async () => {
    try { const r = await lookupSite(siteId); setData(r.data); }
    catch { toast.error("Could not load site"); }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-700" /></div>;

  const { site, client, contacts, access_notes, work_orders, inspections, visits } = data;
  const targets = [{ target: "site", target_id: site.id, label: `Site: ${site.name}` }, ...(client ? [{ target: "client", target_id: client.id, label: `Customer: ${client.name}` }] : [])];
  const shownVisits = allVisits ? visits : visits.slice(0, 8);

  return (
    <div className="space-y-2" data-testid="lookup-site-page">
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold text-slate-900 leading-tight break-words" data-testid="lookup-site-name">{site.name}</h2>
            {client && (
              <button type="button" onClick={() => openClient(client.id)} className="text-[11px] text-slate-600 hover:text-blue-700 inline-flex items-center gap-1" data-testid="lookup-site-client-link">
                <Building2 className="w-3 h-3" />{client.name}
              </button>
            )}
            {site.address_full && (
              <button type="button" onClick={() => openMaps(site.address_full)} className="block text-[11px] text-blue-600 hover:underline text-left mt-0.5">{site.address_full}</button>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
        <AccessNotesPanel notes={access_notes} targets={targets} onChanged={load} compact />
      </div>

      <Section title={tr("Contacts")} count={contacts.length} testId="lookup-site-contacts">
        {contacts.length === 0 ? <Empty text="No contacts on file." /> : contacts.map((c, i) => <ContactRow key={c.id || i} c={c} />)}
      </Section>

      <Section title={tr("Work Orders at this site")} count={work_orders.length} testId="lookup-site-wos">
        {work_orders.length === 0 ? <Empty text="No work orders at this site." /> : work_orders.map(w => <WORow key={w.id} wo={w} onOpen={openWO} />)}
      </Section>

      <Section title={tr("Inspections")} count={inspections.length} testId="lookup-site-inspections">
        {inspections.length === 0 ? <Empty text="No inspections yet." /> : inspections.map(i => <InspectionRow key={i.id} insp={i} onOpen={openInspection} />)}
      </Section>

      <Section title={tr("Visit history")} count={visits.length} testId="lookup-site-visits">
        {visits.length === 0 ? <Empty text="No visits logged." /> : shownVisits.map(v => <VisitRow key={v.id} v={v} />)}
        {visits.length > 8 && (
          <button type="button" onClick={() => setAllVisits(a => !a)} className="w-full text-[10px] text-blue-600 hover:underline py-1" data-testid="lookup-site-visits-toggle">
            {allVisits ? "Show fewer" : `Show all ${visits.length} visits`}
          </button>
        )}
      </Section>
    </div>
  );
}
